// Client-side cache for grow/inventory/recipe photos, keyed by the STABLE PATH portion
// of a presigned MinIO URL (everything before the "?") rather than the full URL. MinIO's
// presigned URLs embed the object's storage key in the path and rotate only the query
// string (signature + expiry) on every request — so the path changes exactly when the
// underlying image bytes change (upload/replace) and stays constant otherwise. That makes
// it a correct cache key without needing any photo-id-based invalidation scheme.
//
// Uses the Cache Storage API (same mechanism as service workers) so cached bytes survive
// across page loads and browser restarts — this is what lets a previously-seen photo
// paint instantly instead of showing a loading gap while the fresh copy re-fetches.

const CACHE_NAME = "fungistics-photos-v1";

function cacheKey(url) {
  try {
    const u = new URL(url, window.location.origin);
    return u.origin + u.pathname;
  } catch {
    return url.split("?")[0];
  }
}

function supportsCacheAPI() {
  return typeof window !== "undefined" && "caches" in window;
}

// Returns a local object URL for the cached copy of `url`, or null if not cached yet.
// Caller is responsible for revoking the object URL (via URL.revokeObjectURL) once no
// longer displayed, to avoid leaking blob references.
export async function getCachedPhotoURL(url) {
  if (!url || !supportsCacheAPI()) return null;
  try {
    const cache = await caches.open(CACHE_NAME);
    const match = await cache.match(cacheKey(url));
    if (!match) return null;
    const blob = await match.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null; // cache errors should never block showing the live photo
  }
}

// Fetches `url` fresh and stores it under its stable path key, replacing any previous
// entry for that path (i.e. previous storage-key generation for the same photo, if any
// old entry happens to share a path prefix — in practice each replace gets a brand new
// path, so old entries just age out unused; we don't proactively evict them here).
export async function cachePhoto(url) {
  if (!url || !supportsCacheAPI()) return;
  try {
    const res = await fetch(url);
    if (!res.ok) return;
    const cache = await caches.open(CACHE_NAME);
    await cache.put(cacheKey(url), res.clone());
  } catch {
    // best-effort — a failed cache write shouldn't surface to the UI
  }
}
